import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import toast from 'react-hot-toast';

const isDev = import.meta.env.DEV;

/**
 * In dev, fires a detailed toast on every query/mutation error so failures
 * are immediately identifiable without opening DevTools.
 * In production, errors are surfaced inline by the page component instead.
 */
function errorToast(error: unknown, context: string): void {
  if (!isDev) return;
  const message = error instanceof Error ? error.message : String(error);
  // Truncate very long server responses so the toast stays readable
  const truncated = message.length > 300 ? `${message.slice(0, 300)}…` : message;
  toast.error(`[DEV] ${context}\n${truncated}`, { duration: 8000 });
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      const key = JSON.stringify(query.queryKey);
      errorToast(error, `Query failed: ${key}`);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      const key = mutation.options.mutationKey
        ? JSON.stringify(mutation.options.mutationKey)
        : '(unnamed mutation)';
      errorToast(error, `Mutation failed: ${key}`);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});
